import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './AllFeatures.module.scss';
import VormatsAPI from '../../../../../../../common/apis/VormatsAPI';
import Spinner from '../../../../../../../common/components/Spinner';
import cx from 'classnames';
import usePagination from '../../../../../../hooks/usePagination';
import Pagination from '../../../../../../components/Pagination/Pagination';
import SearchComponent from '../../../../../../components/SearchComponent/SearchComponent';
import EditFeatureModal from '../EditFeatureModal/EditFeatureModal';
import { CustomButton } from '../../../../../../components/Button/Button';
import Icon from '../../../../../../components/Icon/Icon';
import { useTranslation } from 'react-i18next';
import Toggle from '../../../../../../components/Toggle/Toggle';

const AllFeatures = () => {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedFeature, setSelectedFeature] = useState(null);
    const [isCreateMode, setIsCreateMode] = useState(false);

    const { t } = useTranslation();

    const getFeaturesList = useCallback(async params => {
        return await VormatsAPI.getAllFeatures(params);
    }, []);

    const {
        list: features,
        query,
        totalData,
        isLoading: loading,
        onSearch,
        onChangePage,
        loadList
    } = usePagination({
        service: getFeaturesList,
        itemsPerPage: 5,
        sortByFiled: 'id',
    });

    const scrollableContentRef = useRef(null);

    const isListScrollable = useMemo(() => {
        const { scrollHeight, offsetHeight } = scrollableContentRef?.current || {};
        return scrollHeight - offsetHeight > 0;
    }, [features]);

    useEffect(() => {
        loadList();
    }, []);

    const handleOpenEditModal = feature => {
        setSelectedFeature(feature);
        setEditModalOpen(true);
        setIsCreateMode(false);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedFeature(null);
        setIsCreateMode(false);
    };

    const handleSaveEdit = async updatedFeature => {
        if (isCreateMode) {
            await VormatsAPI.createFeature(updatedFeature);
        } else {
            await VormatsAPI.updateFeature(updatedFeature.id, updatedFeature);
        }
        handleCloseEditModal();
        loadList();
    };

    const handleCreateFeature = () => {
        setSelectedFeature({ name: '', default: 0, default_value: '' });
        setEditModalOpen(true);
        setIsCreateMode(true);
    };

    const handleDeleteFeature = async (feature, e) => {
        e.stopPropagation();
        if (window.confirm(`${t('company.settings.features.delete_confirm')} "${feature.name}"?`)) {
            await VormatsAPI.deleteFeature(feature.id);
            loadList();
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.headerContainer}>
                <h2 className={styles.header}>{t('company.settings.features.all.title')} ({totalData || 0})</h2>
                <SearchComponent searchValue={query.search} onChange={onSearch} clearButton />
            </div>
            <div className={styles.featuresTable}>
                <div className={styles.featuresTableHeader}>
                    <div className={styles.itemHeader}>
                        <span>{t('company.settings.features.name')}</span>
                    </div>
                </div>
                {loading ? (
                    <div className={styles.spinnerWrapper}>
                        <Spinner size="3px" width="40px" height="40px" />
                    </div>
                ) : (
                    <ul ref={scrollableContentRef} className={cx(styles.featureList, { [styles.scrollableFeatureList]: isListScrollable })}>
                        {features.map((feature) => (
                            <li key={feature.id} className={styles.featureItem} onClick={() => handleOpenEditModal(feature)}>
                                <span className={styles.featureName}>{feature.publicName || feature.name}</span>
                                <div className={styles.actionsContainer}>
                                    <Toggle
                                        id={`feature-${feature.id}`}
                                        checked={!!feature.default}
                                        onClick={e => e.stopPropagation()}
                                        onChange={async (e) => {
                                            e.stopPropagation();
                                            const isEnabling = !feature.default;
                                            const action = isEnabling ? 'enable' : 'disable';
                                            if (!window.confirm(
                                                `This will ${action} this feature for ALL the companies (except the ones where it's explicitly set differently).\n\nAre you sure you want to ${action} this feature?`
                                            )) {
                                                return;
                                            }
                                            const newState = feature.default ? 0 : 1;
                                            const updatedFeature = { ...feature, default: newState };
                                            await VormatsAPI.updateFeature(feature.id, updatedFeature);
                                            loadList();
                                        }}
                                        className={styles.toggle}
                                    />
                                    <CustomButton
                                        variant="link"
                                        className={styles.removeBtn}
                                        onClick={e => handleDeleteFeature(feature, e)}
                                        ariaLabel="Delete feature"
                                    >
                                        <Icon src="bin_red" className={styles.binIcon} />
                                    </CustomButton>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div className={styles.footerContainer}>
                <CustomButton variant="primary" label={t('general.create') + ' feature'} onClick={handleCreateFeature} className={styles.createBtn} />
                {!!totalData && (
                    <Pagination currentPage={query.page} totalPages={Math.ceil(totalData / query.perPage)} changePage={onChangePage} />
                )}
            </div>
            <EditFeatureModal
                open={editModalOpen}
                featureData={selectedFeature}
                onClose={handleCloseEditModal}
                onSave={handleSaveEdit}
                isCreateMode={isCreateMode}
            />
        </div>
    );
};

export default AllFeatures; 